import { Suspense } from 'react';
import { Await, useLoaderData, defer, useParams } from 'react-router-dom';
import {
  Button,
  H1,
  H3,
  Table,
  TableBody,
  TableData,
  TableHead,
  TableLoading,
  TableRow,
} from 'ui-rollup';
import { Bill } from '../../types/bill';
import styles from './detail.module.css';
import Skeleton from 'react-loading-skeleton';
import { updatePaymentStatus } from '../services';
import { fetchData } from '../../utils/fetchData';
import { getJwt } from '../../utils/getJwt';

export const loader = ({ params }) => {
  const detail = fetchData<Bill>(
    `${process.env.REACT_APP_BACKEND_API}/bills/${params.billId}`,
    {
      headers: {
        Authorization: `Bearer ${sessionStorage.getItem('token')}`,
      },
    },
  );

  return defer({ detail });
};

export const Detail = () => {
  const data = useLoaderData() as { detail: Bill };
  const { billId } = useParams();

  return (
    <>
      <header className={styles.detailHeaderContainer}>
        <H1>
          <Suspense fallback={<Skeleton />}>
            <Await resolve={data.detail}>{({ billId }) => billId}</Await>
          </Suspense>{' '}
          Detail 🧐
        </H1>
        <div className={styles.detailHeaderActionContainer}>
          <Button disabled>Share</Button>
          <a
            href={`${process.env.REACT_APP_BACKEND_API}/bills/${billId}/users/${getJwt().id}/download`}
            target="_blank"
            rel="noreferrer"
          >
            <Button>📥 Download</Button>
          </a>
        </div>
      </header>
      <div className={styles.userBillSectionContainer}>
        <div>
          <Text label="Name">
            <Suspense fallback={<Skeleton />}>
              <Await resolve={data.detail}>
                {({ client }) => {
                  return (
                    <span className={styles.textValue}>{client['name']}</span>
                  );
                }}
              </Await>
            </Suspense>
          </Text>
          <Text label="Email">
            <Suspense fallback={<Skeleton />}>
              <Await resolve={data.detail}>
                {({ client }) => {
                  return (
                    <span className={styles.textValue}>{client['email']}</span>
                  );
                }}
              </Await>
            </Suspense>
          </Text>
          <Text label="Company">
            <Suspense fallback={<Skeleton />}>
              <Await resolve={data.detail}>
                {({ client }) => {
                  return (
                    <span className={styles.textValue}>
                      {client['companyName']}
                    </span>
                  );
                }}
              </Await>
            </Suspense>
          </Text>
          <Text label="Address">
            <Suspense fallback={<Skeleton />}>
              <Await resolve={data.detail}>
                {({ client }) => {
                  return (
                    <span className={styles.textValue}>
                      {client['address']}
                    </span>
                  );
                }}
              </Await>
            </Suspense>
          </Text>
          <Text label="GST">
            <Suspense fallback={<Skeleton />}>
              <Await resolve={data.detail}>
                {({ client }) => {
                  return (
                    <span className={styles.textValue}>{client['gst']}</span>
                  );
                }}
              </Await>
            </Suspense>
          </Text>
          <Text label="Phone">
            <Suspense fallback={<Skeleton />}>
              <Await resolve={data.detail}>
                {({ client }) => {
                  return (
                    <span className={styles.textValue}>{client['phone']}</span>
                  );
                }}
              </Await>
            </Suspense>
          </Text>
        </div>
        <div>
          <Text label="Date of issue">
            <Suspense fallback={<Skeleton />}>
              <Await resolve={data.detail}>
                {(detail) => {
                  return (
                    <span className={styles.textValue}>
                      {new Date(detail['dateOfIssue'] ?? '').toLocaleDateString(
                        undefined,
                        {
                          month: 'long',
                          day: 'numeric',
                          year: 'numeric',
                        },
                      )}
                    </span>
                  );
                }}
              </Await>
            </Suspense>
          </Text>
          <Text label="Created">
            <Suspense fallback={<Skeleton />}>
              <Await resolve={data.detail}>
                {(detail) => {
                  return (
                    <span className={styles.textValue}>
                      {new Date(detail['created'] ?? '').toLocaleDateString(
                        undefined,
                        {
                          month: 'long',
                          day: 'numeric',
                          year: 'numeric',
                        },
                      )}
                    </span>
                  );
                }}
              </Await>
            </Suspense>
          </Text>
          <Text label="Is paid">
            <Suspense fallback={<Skeleton />}>
              <Await resolve={data.detail}>
                {(detail) => {
                  // May need to add disabled prop to prevent toggle spamming
                  return (
                    <input
                      style={{
                        transform: 'scale(1.2)',
                      }}
                      type="checkbox"
                      // disabled={detail["isPaid"]}
                      defaultChecked={detail['isPaid']}
                      onChange={() =>
                        updatePaymentStatus(billId, !data.detail.isPaid)
                      }
                    />
                  );
                }}
              </Await>
            </Suspense>
          </Text>
        </div>
      </div>
      <div>
        <Table>
          <TableRow>
            <TableHead>#</TableHead>
            <TableHead>Desc</TableHead>
            <TableHead>Qty</TableHead>
            <TableHead>Rate</TableHead>
            <TableHead>GST</TableHead>
            <TableHead>IGST</TableHead>
            <TableHead>Amount</TableHead>
          </TableRow>
          <TableBody>
            <Suspense fallback={<TableLoading rowCount={5} columnCount={6} />}>
              <Await resolve={data.detail}>
                {({ products }: Bill) =>
                  products.map((product, index) => (
                    <TableRow key={product.id}>
                      <TableData>{index + 1}</TableData>
                      <TableData>{product.description}</TableData>
                      <TableData>{product.quantity}</TableData>
                      <TableData>{product.rate}</TableData>
                      <TableData>{product.gst}</TableData>
                      <TableData>{product.isIgst ? 'Yes' : 'No'}</TableData>
                      <TableData>₹ {product.amount}</TableData>
                    </TableRow>
                  ))
                }
              </Await>
            </Suspense>
          </TableBody>
        </Table>
      </div>
      <footer className={styles.footerContainer}>
        <H3>
          Total: ₹{' '}
          <Suspense fallback={<Skeleton />}>
            <Await resolve={data.detail}>{({ total }) => total}</Await>
          </Suspense>
        </H3>
      </footer>
    </>
  );
};

const Text = ({
  label,
  children,
}: {
  label: string;
  children: JSX.Element;
}) => (
  <p className={styles.textContainer}>
    {label}: {children}
  </p>
);
